import { differenceInDays, endOfDay, endOfToday, isPast } from 'date-fns';
import {
  ActionPlanTaskSchema,
  ActionPlanTasksSchema,
} from '../../../definitions/schemas';
import { ActionPlanTaskStatus } from '../../../enums/ActionPlans';
import { getDateFromISO } from '../../../lib/Date';

const DUE_SOON_THRESHOLD = 7;

export const getISODueDate = (dueDate: Date | null): string | undefined => {
  if (!dueDate) {
    return undefined;
  }

  // Do NOT use dueDate.toISOString() because it outputs an UTC value
  // Alternative date-fns.format(dueDate, lib/Date/DATE_ISO_FORMAT)
  return [
    dueDate.getFullYear(),
    (dueDate.getMonth() + 1).toString().padStart(2, '0'),
    dueDate.getDate().toString().padStart(2, '0'),
  ].join('-');
};

export const isTaskCompleted = (task: Partial<ActionPlanTaskSchema>): boolean =>
  task.status === ActionPlanTaskStatus.Completed &&
  (!task.subtasks?.length ||
    !task.subtasks.some((subtask) => !isTaskCompleted(subtask)));

export const isTaskDueSoon = (task: Partial<ActionPlanTaskSchema>) => {
  if (!task.dueDate) {
    return false;
  }

  // 1. Get the deadline, which is the end of the date defined by the due date.
  //    In this scenario, due soon tasks should have a due date in the future
  const deadline = endOfDay(getDateFromISO(task.dueDate));
  // 2. Calculate the difference in days since today's end of the day and
  //    the end of the due date
  const difference = differenceInDays(deadline, endOfToday());
  // 3. And check if it's within the threshold to be considered a "due soon" task
  const isDueSoon = difference >= 0 && difference <= DUE_SOON_THRESHOLD;

  return !isTaskCompleted(task) && isDueSoon;
};

export const isTaskOverdue = (task: Partial<ActionPlanTaskSchema>) => {
  if (!task.dueDate) {
    return false;
  }

  // 1. Get the deadline, which is the end of the date defined by the due date
  const deadline = endOfDay(getDateFromISO(task.dueDate));
  // 2. Check if that deadline is in the past, according to today's date
  const isOverdue = isPast(deadline);

  return !isTaskCompleted(task) && isOverdue;
};

export const getCategorizedTasks = (tasks: ActionPlanTasksSchema) => {
  const completed: ActionPlanTasksSchema = [];
  const dueSoon: ActionPlanTasksSchema = [];
  const overdue: ActionPlanTasksSchema = [];

  tasks.forEach((task) => {
    if (isTaskCompleted(task)) {
      completed.push(task);
    } else if (isTaskDueSoon(task)) {
      dueSoon.push(task);
    } else if (isTaskOverdue(task)) {
      overdue.push(task);
    }
  });

  return [completed, dueSoon, overdue];
};
